import React, { useEffect, useState, useMemo } from 'react';
import { Typography, TextField, MenuItem, Alert, Box, Paper } from '@mui/material';
import Button from 'Components/Button';
import withAppInsights from 'Helpers/appInsights';
import { PushNotificationModuleStore } from './PushNotificationModuleStore';
import { useOidcUser } from '@axa-fr/react-oidc';
import { useNavigate } from 'react-router-dom';
import { HOME } from '../../../initialization/Routes/RoutesConfig';
import {
  FormContainer,
  ContentContainer,
  Footer,
  ErrorMessage,
  SuccessMessage,
  StyledTextField,
  InfoBox,
} from './styles';

function PushNotifications() {
  const { oidcUser } = useOidcUser();
  const navigate = useNavigate();
  const notificationStore = PushNotificationModuleStore.Instance().Use();
  const [loading, setLoading] = useState(true);
  const hasPermissions = notificationStore.HasPermissions;

  // Form state
  const [userIdsInput, setUserIdsInput] = useState('');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [notificationMethodId, setNotificationMethodId] = useState(2); // Default to push notification
  const [userIdsError, setUserIdsError] = useState('');

  const error = notificationStore.Error;
  const success = notificationStore.Success;
  const isLoading = notificationStore.IsLoading;

  useEffect(() => {
    if (oidcUser && loading) {
      notificationStore.initialize({ userId: oidcUser?.sub }).then(() => setLoading(false));
    }
  }, [oidcUser]);

  useEffect(() => {
    if (hasPermissions === false) {
      navigate(HOME);
    }
  }, [hasPermissions]);

  const validateUserIds = (input) => {
    if (!input || input.trim() === '') {
      setUserIdsError('');
      return [];
    }

    // Split by comma and trim whitespace
    const userIdStrings = input.split(',').map((id) => id.trim()).filter((id) => id !== '');
    
    // Check if all are valid numbers
    const invalidIds = userIdStrings.filter((id) => isNaN(id) || id === '');
    if (invalidIds.length > 0) {
      setUserIdsError('All User IDs must be valid numbers separated by commas.');
      return null;
    }

    // Check count
    if (userIdStrings.length > 500) {
      setUserIdsError(`You entered ${userIdStrings.length} User IDs. Maximum 500 User IDs allowed per request.`);
      return null;
    }

    setUserIdsError('');
    return userIdStrings.map((id) => parseInt(id, 10));
  };

  const handleUserIdsChange = (event) => {
    const input = event.target.value;
    setUserIdsInput(input);
    validateUserIds(input);
  };

  const handleSubmit = async () => {
    notificationStore.clearMessages();
    
    const userIds = validateUserIds(userIdsInput);
    if (!userIds || userIds.length === 0) {
      notificationStore.Error = 'Please enter at least one User ID.';
      return;
    }

    if (userIdsError) {
      return; // Don't submit if there's a validation error
    }

    const success = await notificationStore.sendNotifications(userIds, title, message, notificationMethodId);
    
    if (success) {
      // Clear form on success
      setUserIdsInput('');
      setTitle('');
      setMessage('');
      setNotificationMethodId(2);
    }
  };

  const handleClear = () => {
    setUserIdsInput('');
    setTitle('');
    setMessage('');
    setNotificationMethodId(2);
    setUserIdsError('');
    notificationStore.clearMessages();
  };

  // Memoize validation to prevent infinite re-renders
  // Must be before early return to follow Rules of Hooks
  const { userIds, userIdCount } = useMemo(() => {
    if (!userIdsInput || userIdsInput.trim() === '') {
      return { userIds: [], userIdCount: 0 };
    }

    const userIdStrings = userIdsInput.split(',').map((id) => id.trim()).filter((id) => id !== '');
    const invalidIds = userIdStrings.filter((id) => isNaN(id) || id === '');
    
    if (invalidIds.length > 0 || userIdStrings.length > 500) {
      return { userIds: null, userIdCount: 0 };
    }

    const parsedIds = userIdStrings.map((id) => parseInt(id, 10));
    return { userIds: parsedIds, userIdCount: parsedIds.length };
  }, [userIdsInput]);

  if (loading || !hasPermissions) {
    return (
      <Typography fontSize={18} color="red">
        Loading...
      </Typography>
    );
  }

  return (
    <FormContainer>
      <Typography component="h1" fontSize={24} fontWeight={700} marginBottom={2}>
        Send Push Notifications
      </Typography>

      <InfoBox>
        <Alert severity="info">
          Send push notifications to up to 500 users at once. Enter User IDs separated by commas.
        </Alert>
      </InfoBox>

      <ContentContainer>
        <StyledTextField
          fullWidth
          label="User IDs (comma-separated)"
          placeholder="e.g., 123, 456, 789"
          value={userIdsInput}
          onChange={handleUserIdsChange}
          error={!!userIdsError}
          helperText={
            userIdsError || 
            (userIdCount > 0 ? `${userIdCount} User ID(s) entered` : 'Enter User IDs separated by commas')
          }
          multiline
          rows={4}
          variant="outlined"
        />

        <StyledTextField
          fullWidth
          label="Notification Method"
          select
          value={notificationMethodId}
          onChange={(e) => setNotificationMethodId(parseInt(e.target.value, 10))}
          variant="outlined"
        >
          <MenuItem value={1}>All</MenuItem>
          <MenuItem value={2}>Push Notification</MenuItem>
          <MenuItem value={3}>SMS</MenuItem>
        </StyledTextField>

        <StyledTextField
          fullWidth
          label="Title"
          placeholder="Enter notification title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          variant="outlined"
          inputProps={{ maxLength: 100 }}
          helperText={`${title.length}/100 characters`}
        />

        <StyledTextField
          fullWidth
          label="Message"
          placeholder="Enter notification message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          multiline
          rows={6}
          variant="outlined"
          inputProps={{ maxLength: 500 }}
          helperText={`${message.length}/500 characters`}
        />
      </ContentContainer>

      <Footer>
        <Button onClick={handleClear} variant={Button.variant.OUTLINED} disabled={isLoading}>
          Clear
        </Button>
        <Button 
          onClick={handleSubmit} 
          loading={isLoading} 
          disabled={!!userIdsError || userIdCount === 0 || !title || !message}
        >
          Send Notifications
        </Button>
      </Footer>

      {error && <ErrorMessage>{typeof error === 'string' ? error : JSON.stringify(error)}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}
    </FormContainer>
  );
}

export default withAppInsights(PushNotifications);

